import { useEffect, useState } from 'react';

/**
 * Reports an image's intrinsic width/height ratio.
 *
 * The landing page banners are uploaded through the admin, so their
 * proportions are not known at build time — the promo strip is 7.5:1
 * while the hero is 3:2. Hard-coding one `aspect-ratio` for all of
 * them cropped whichever did not match. This measures the real asset
 * and lets the container adopt its shape.
 *
 * `fallback` is used until the intrinsic size is known, so the layout
 * reserves space instead of collapsing to zero height and shifting.
 *
 * The probe reuses the browser's image cache, so it costs no extra
 * network request beyond the <img> that renders anyway.
 */
const useNaturalAspectRatio = (src, fallback = null) => {
  const [ratio, setRatio] = useState(null);

  useEffect(() => {
    if (!src) {
      setRatio(null);
      return undefined;
    }

    let active = true;
    const image = new Image();

    image.onload = () => {
      if (active && image.naturalWidth > 0 && image.naturalHeight > 0) {
        setRatio(image.naturalWidth / image.naturalHeight);
      }
    };
    // A broken image keeps the fallback rather than collapsing the box.
    image.onerror = () => {
      if (active) setRatio(null);
    };
    image.src = src;

    return () => {
      active = false;
      image.onload = null;
      image.onerror = null;
    };
  }, [src]);

  return ratio ?? fallback;
};

export default useNaturalAspectRatio;
